import { useCallback, useEffect, useState } from "react";
import { enteteTenant } from "../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Cache simple en mémoire (évite de refetch à chaque mount)
let _cache = { pipeline: null, tenantId: null, promise: null };

/**
 * Hook : lit le pipeline métier depuis la gouvernance MongoDB.
 * Source unique de vérité — JAMAIS hardcoder les étapes ailleurs.
 *
 * @param {object} options
 * @param {string} options.tenantId - tenant actif (défaut: tef)
 * @param {string} options.name - nom du pipeline (défaut: tef_pipeline_amo)
 * @returns {{pipeline, steps, legacyMapping, mapLegacy, loading, error, reload}}
 */
// 🔴🔴 N°705-1 — LE TENANT COURANT, ⛔ PLUS « tef » EN DUR.
//
// ⚠️ MESURÉ : la signature valait `{tenantId = "tef", name = "tef_pipeline_amo"}`
// et `PipelineStepper` appelait `usePipeline()` SANS ARGUMENT. ⇒ TOUT tenant
// — mario_bat, omv_france, climatpac — demandait la gouvernance de TEF, avec
// un en-tête `X-Tenant-ID: tef` FORGÉ CÔTÉ ÉCRAN, et affichait les libellés
// du pipeline AMO de TEF sur SES dossiers.
// 🔑 Ce ⛔ n'était PAS un défaut de garde : c'était un REPLI CODÉ EN DUR SUR
// UN SLUG — la même famille que le `or "climatpac"` du N°702. Un repli de ce
// genre ⛔ n'est jamais neutre : il désigne le tenant dont on lit les données.
// ⚠️ MESURÉ EN BASE : `governance_pipeline` ⛔ n'existe QUE pour `tef`.
// ⇒ Les autres tenants ⛔ n'auront PLUS de pipeline tant qu'ils ⛔ n'en
// déclarent pas — c'est UNE LIGNE DE CONFIGURATION, ⛔ pas du code, et
// l'écran DIT désormais « rien » au lieu d'afficher le pipeline d'autrui.
const TENANT_COURANT = () => {
  try {
    return localStorage.getItem("current_tenant_slug")
        || localStorage.getItem("current_tenant") || null;
  } catch (e) { return null; }
};

export default function usePipeline({ tenantId, name = "tef_pipeline_amo" } = {}) {
  tenantId = tenantId || TENANT_COURANT();
  const [pipeline, setPipeline] = useState(
    _cache.pipeline && _cache.tenantId === tenantId ? _cache.pipeline : null
  );
  const [loading, setLoading] = useState(!pipeline);
  const [error, setError] = useState(null);

  const fetchPipeline = useCallback(async () => {
    // Mutualise les requêtes parallèles
    if (_cache.promise && _cache.tenantId === tenantId) {
      return _cache.promise;
    }
    const token = localStorage.getItem("session_token");
    const headers = {
      ...enteteTenant(tenantId),
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    const p = fetch(`${API}/api/governance/pipeline?name=${encodeURIComponent(name)}`, {
      headers, credentials: "include",
    }).then(async (res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    });
    _cache.promise = p;
    _cache.tenantId = tenantId;
    try {
      const data = await p;
      _cache.pipeline = data;
      return data;
    } finally {
      _cache.promise = null;
    }
  }, [tenantId, name]);

  const reload = useCallback(async () => {
    _cache = { pipeline: null, tenantId: null, promise: null };
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPipeline();
      setPipeline(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [fetchPipeline]);

  useEffect(() => {
    if (pipeline) return;
    let cancelled = false;
    setLoading(true);
    fetchPipeline()
      .then((data) => { if (!cancelled) { setPipeline(data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchPipeline, pipeline]);

  const steps = pipeline?.steps || [];
  const legacyMapping = pipeline?.legacy_mapping || {};
  const closureReasons = pipeline?.closure_reasons || {};
  const semanticGroups = pipeline?.semantic_groups || {};
  const legacyClosureCodes = pipeline?.legacy_closure_codes || [];

  /** Convertit un code legacy → code cible (ou renvoie tel quel si déjà cible). */
  const mapLegacy = useCallback(
    (legacyCode) => legacyMapping[legacyCode] || legacyCode || null,
    [legacyMapping]
  );

  /** Retourne l'objet step depuis son code. */
  const getStep = useCallback(
    (code) => steps.find((s) => s.code === code) || null,
    [steps]
  );

  // === Helpers sémantiques (source unique : governance_pipeline) ===

  /** True si le statut (legacy ou cible) est actif (non clôturé). */
  const isActive = useCallback(
    (statut) => {
      if (!statut) return false;
      const target = mapLegacy(statut);
      const active = semanticGroups.active_codes || [];
      if (active.length) return active.includes(target);
      const finals = [
        ...(semanticGroups.final_win_codes || []),
        ...(semanticGroups.final_lose_codes || []),
      ];
      return !finals.includes(target);
    },
    [mapLegacy, semanticGroups]
  );

  /** True si le statut correspond à un rejet/abandon. */
  const isRejected = useCallback(
    (statut) => {
      if (!statut) return false;
      const target = mapLegacy(statut);
      return (semanticGroups.final_lose_codes || []).includes(target);
    },
    [mapLegacy, semanticGroups]
  );

  /** True si le statut est post-dépôt ANAH. */
  const isPostDepot = useCallback(
    (statut) => {
      if (!statut) return false;
      const target = mapLegacy(statut);
      return (semanticGroups.post_depot_codes || []).includes(target);
    },
    [mapLegacy, semanticGroups]
  );

  /** True si le statut legacy (brut) déclenche une clôture (date_cloture). */
  const isClosureLegacy = useCallback(
    (statut) => {
      if (!statut) return false;
      return legacyClosureCodes.includes(statut);
    },
    [legacyClosureCodes]
  );

  /** Code du statut initial par défaut (fallback 'cree'). */
  const initialDefaultCode = semanticGroups.initial_default_code || "cree";

  /** Phase haut-niveau (prospection, operation, conformite, ...) ou null. */
  const getPhase = useCallback(
    (statut) => {
      if (!statut) return null;
      const target = mapLegacy(statut);
      return (semanticGroups.phase_mapping || {})[target] || null;
    },
    [mapLegacy, semanticGroups]
  );

  return {
    pipeline,
    steps,
    legacyMapping,
    closureReasons,
    semanticGroups,
    mapLegacy,
    getStep,
    isActive,
    isRejected,
    isPostDepot,
    isClosureLegacy,
    initialDefaultCode,
    getPhase,
    loading,
    error,
    reload,
  };
}
